// Contact optimisation (the customer), Pulse screen.
// Mirrors backend/services/contact_optimization/service.py::get_pulse_summary.
// Build step 2: direct-query metrics only. Model-quality metrics
// (lift at capacity, predicted vs. actual) used to live here too, but they
// never move week to week (they're frozen at the last training run) and
// are already fully covered on the model page (Predictive Models >
// Contact Optimization Scoring) -- removed from Pulse 2026-08-03 so every
// number on this page is a live weekly comparison.

// Every section route below is wrapped in the backend's `with_section_cache`
// (see cache.py) -- it always stamps the payload with when it was actually
// computed and whether that came from the cache or a live recompute, so the
// tab bar's "Updated ..." label and Refresh button never have to guess.
// Optional only because a handful of very old cache rows can predate this
// field; every live compute always sets it.
export interface SectionCacheMeta {
  computed_at?: string;
  from_cache?: boolean;
  // True when the payload was past its TTL but served anyway while the
  // backend rebuilt it on a background thread (see cache.py's
  // stale-while-revalidate path) -- `computed_at` is still the real age,
  // so the "Updated ..." label stays honest without any extra handling
  // here. Nothing in the UI branches on this today; it's the signal to
  // use if we ever want a "refreshing..." hint.
  stale?: boolean;
}

export interface PulsePeriod {
  current_start: string;
  current_end: string;
  baseline_start: string;
  baseline_end: string;
  anchor_dt: string;
}

export interface PulseLiftDetail {
  fraction: number;
  n_in_top: number;
  top_connect_rate: number;
  overall_connect_rate: number;
  lift_vs_random: number | null;
}

export interface PulsePredictedVsActualDetail {
  predicted_connects: number;
  actual_connects: number;
  ratio: number | null;
}

// DESIGN.md section 8.2: unit economics. Spend is lead_v.est_spend_amt,
// summed once per distinct lead dialed in the period (not per call).
// Premium is windowed CAS premium x a live submit-to-paid rate -- see
// queries.py::pulse_unit_economics_sql for the exact attribution window.
export interface PulseUnitEconomics {
  total_spend_current: number;
  total_spend_baseline: number;
  leads_dialed_current: number;
  leads_with_spend_current: number;
  spend_coverage_pct_current: number | null;
  cost_per_dial_current: number | null;
  cost_per_dial_baseline: number | null;
  cost_per_connect_current: number | null;
  cost_per_connect_baseline: number | null;
  cost_per_application_current: number | null;
  cost_per_application_baseline: number | null;
  value_cost_ratio_current: number | null;
  value_cost_ratio_baseline: number | null;
  submit_to_paid_rate: number | null;
}

export interface PulseHeader {
  // Plain activity counts, not ratios -- "how much did we even dial this
  // week." Baseline is a per-week average (the 4-week baseline total / 4),
  // so it's directly comparable to one week of "current."
  total_calls_current: number;
  total_calls_baseline_weekly_avg: number;
  total_connects_current: number;
  total_connects_baseline_weekly_avg: number;
  connect_rate_current: number | null;
  connect_rate_baseline: number | null;
  connect_rate_delta_pp: number | null;
  // "connect" and "contact" used to be used interchangeably for this same
  // 90-second-connect concept across this page -- standardized on
  // "connect" everywhere 2026-08-03. Denominator still uses agent-hour
  // cells from pulse_agent_hours_sql (the raw agent-hours tile was removed).
  connects_per_agent_hour_current: number | null;
  connects_per_agent_hour_baseline: number | null;
  connects_per_agent_hour_delta: number | null;
  // DESIGN.md section 8.1: application completion (call_disposition
  // 'Quoted' or 'Carrier App Submitted' variants) -- a denser intermediate
  // conversion than CAS/premium. Rate is of total calls, not of connects.
  app_complete_current: number;
  app_complete_baseline: number;
  app_complete_rate_current: number | null;
  app_complete_rate_delta_pp: number | null;
  unit_economics: PulseUnitEconomics;
}

export type PoolingBand = 'prime' | 'good' | 'fill' | 'avoid';

// Build step 6: the pickup-probability model.
// Mirrors backend/routers/contact_optimization.py's _serialize_model_row.

export type ModelRunStatus = 'training' | 'active' | 'failed' | 'archived';

export interface ModelCalibrationBin {
  bin_low: number;
  bin_high: number;
  predicted_mean: number;
  actual_rate: number;
  count: number;
}

export interface ModelLeakageTest {
  passed: boolean;
  flagged_features: string[];
  per_feature_auc: Record<string, number>;
}

export interface ModelMetrics {
  auc_lightgbm: number;
  auc_logistic_challenger: number;
  lift_at_capacity: PulseLiftDetail;
  lift_at_capacity_logistic_challenger: PulseLiftDetail;
  predicted_vs_actual_totals: PulsePredictedVsActualDetail;
  calibration_bins: ModelCalibrationBin[];
  leakage_test: ModelLeakageTest;
  feature_importance: Record<string, number>;
  row_counts: { train: number; calibration: number; validation: number };
}

export interface ContactOptimizationModelRun {
  model_id: string;
  version: number;
  status: ModelRunStatus;
  started_at: string | null;
  trained_at: string | null;
  training_window_start: string | null;
  training_window_end: string | null;
  validation_window_start: string | null;
  validation_window_end: string | null;
  row_count: number | null;
  base_rate: number | null;
  feature_list: string[] | null;
  metrics: ModelMetrics | null;
  error_message: string | null;
}

export interface ModelStatusResponse {
  active: ContactOptimizationModelRun | null;
  training: ContactOptimizationModelRun | null;
  last_failed: ContactOptimizationModelRun | null;
  has_any_version: boolean;
}

export interface ModelHistoryResponse {
  versions: ContactOptimizationModelRun[];
}

export interface PulseFreshLeads {
  definition: string;
  connect_rate_current: number | null;
  connect_rate_baseline: number | null;
  rest_connect_rate_current: number | null;
  rest_connect_rate_baseline: number | null;
  total_calls_current: number;
  total_calls_baseline: number;
  // Optional: absent on any cached response saved before this field existed.
  rest_total_calls_current?: number;
  rest_total_calls_baseline?: number;
}

// One calendar week's totals, company-wide, no state/hour breakdown --
// powers the "over time" trend chart. See queries.py::pulse_weekly_trend_sql.
export interface PulseWeeklyTrendPoint {
  week_start: string;
  total_calls: number;
  total_connects: number;
  connect_rate: number | null;
}

// "Who and when" -- one row per (consumption_day, day_offset), day_offset
// null reserved for "no call yet within the reachability window". See
// queries.py::pulse_cohort_sql for the full reconciliation rule.
export interface PulseCohortRow {
  consumption_day: string | null;
  day_offset: number | null;
  leads: number;
  contacted: number;
  never_contacted: number;
}

// Scheduled appointments and the shared sold/resale pool -- tracked on
// their own terms, deliberately kept out of every other Pulse number.
// See queries.py::pulse_other_populations_sql.
export interface PulseOtherPopulations {
  appointment_calls: number;
  appointments_kept: number;
  appointments_kept_rate: number | null;
  cas_from_appointments: number;
  sold_pool_dials: number;
  sold_pool_connects: number;
  sold_pool_connect_rate: number | null;
  // Optional: absent on any cached response saved before this field existed.
  cas_from_sold_pool?: number;
}

// "What the gap cost" -- fresh leads' hour-of-day timing gaps, converted
// into missed connects, missed sales, and missed dollars. Ported from the
// earlier dialing_opportunity build's headline cards; the benchmark is now
// Timing's own pooled/shrunk best-hour rate (not a raw unpooled pick), and
// the population matches Queue's MQL/non-DNC/non-CAS filter. See
// service.py::_compute_gap_cost.
export interface PulseGapCostDollarBasis {
  avg_premium_per_cas: number;
  avg_commission_per_cas: number;
  is_measured: boolean;
}

export interface PulseGapCost {
  mql_population: number;
  dialed_count: number;
  never_called_count: number;
  mistimed_missed_connects: number;
  never_called_missed_connects: number;
  total_missed_connects: number;
  connect_to_cas_pct: number | null;
  estimated_cas_lost: number | null;
  estimated_premium_lost: number | null;
  estimated_commission_lost: number | null;
  dollar_basis: PulseGapCostDollarBasis;
  states_without_benchmark: string[];
  gross_margin_note: string;
  // Baseline (last-4-weeks) totals, already divided down to a weekly
  // average so they're directly comparable to the current ~7-day totals
  // above -- same convention as PulseHeader's own *_baseline_weekly_avg
  // fields. Optional: absent on any cached response saved before this
  // field existed.
  mql_population_baseline_weekly_avg?: number;
  total_missed_connects_baseline_weekly_avg?: number;
  estimated_cas_lost_baseline_weekly_avg?: number | null;
  estimated_commission_lost_baseline_weekly_avg?: number | null;
}

export interface PulseSummary extends SectionCacheMeta {
  period: PulsePeriod;
  header: PulseHeader;
  fresh_leads: PulseFreshLeads;
  weekly_trend: PulseWeeklyTrendPoint[];
  trend_weeks: number;
  cohort: PulseCohortRow[];
  other_populations: PulseOtherPopulations;
  gap_cost: PulseGapCost;
  ci_level: number;
}

// Timing screen. Mirrors get_timing_heatmap. Build step 5: pooled/shrunk
// connect rate per cell (descriptive). Build step 6 adds a cheap,
// always-on driver_panel (the model's own feature importance) plus a
// separate, heavier on-demand get_timing_adjusted() call for the
// "What the hour causes" model-controlled-for-lead-mix curve.

export interface TimingHour {
  hour: number;
  hour_label: string;
}

export interface TimingDriver {
  label: string;
  share: number;
}

export interface TimingAdjusted {
  available: boolean;
  version: number | null;
  note: string;
  hours?: TimingHour[];
  // Keyed by state, then by hour (JSON object keys are always strings).
  curves: Record<string, Record<string, number>>;
}

export interface TimingCell {
  hour: number;
  hour_label: string;
  total_calls: number;
  total_connects: number;
  connect_rate: number | null;
  // Pooled toward this state's own baseline (build step 5), see stats.py.
  shrunk_rate: number | null;
  ci_low: number | null;
  ci_high: number | null;
  band: PoolingBand | null;
  prior_weight_calls: number | null;
  // This state's own comparison baseline for connect rate -- under the
  // live "raw50" method this is the state's MEDIAN rate (what "Good" vs.
  // "Avoid" is measured against); under "shrunk" it's the prior mean.
  group_median_rate: number | null;
  thin: boolean;
  // Per-slot Value/Cost Ratio (Tier 1 gap #1, CC-PROTOTYPE-AUDIT.md).
  // deal_rate x typical_deal_size x submit_to_paid_rate / spend_per_lead --
  // see service.get_timing_heatmap's docstring for the full formula.
  leads_dialed_value_cost: number;
  deal_rate: number | null;
  typical_deal_size: number | null;
  value_cost_ratio: number | null;
  value_cost_ci_low: number | null;
  value_cost_ci_high: number | null;
  // Deal rate's own pooled band, reused as-is for the dollar ratio too --
  // see the backend docstring for why that's mathematically valid here.
  value_cost_band: PoolingBand | null;
  value_cost_thin: boolean;
  // This state's own comparison baseline for the ratio, in the same "x"
  // units -- the state's median deal rate under "raw50" turned into a
  // ratio, or the prior mean's ratio under "shrunk".
  value_cost_group_median_ratio: number | null;
}

export interface TimingStateRow {
  state: string;
  total_calls: number;
  connect_rate: number | null;
  opportunity_score: number;
  // DESIGN.md section 8.4 concentration guardrail: how many of the
  // measured hours this state actually has >=min_cell_calls volume in.
  hours_with_volume: number;
  below_hours_floor: boolean;
  cells: TimingCell[];
}

export interface TimingStateSummary {
  state: string;
  total_calls: number;
  connect_rate: number | null;
  opportunity_score: number;
  hours_with_volume: number;
  below_hours_floor: boolean;
}

export interface TimingConcentrationGuardrail {
  hours_per_state_floor: number;
  min_volume_to_check: number;
  states_below_floor: string[];
  states_below_floor_count: number;
  states_checked_count: number;
  states_excluded_low_volume_count: number;
}

export interface TimingHeatmap extends SectionCacheMeta {
  hours: TimingHour[];
  grid: TimingStateRow[];
  model_available: boolean;
  model_version: number | null;
  driver_panel: TimingDriver[];
  all_states: TimingStateSummary[];
  default_state_count: number;
  min_cell_calls: number;
  min_cell_deals: number;
  // Single tenant-wide constant (premium-weighted, see queries.
  // submit_to_paid_rate_sql) used to compute every cell's value_cost_ratio.
  submit_to_paid_rate: number | null;
  concentration_guardrail: TimingConcentrationGuardrail;
  ci_level: number;
  date_range: {
    min_call_dt: string | null;
    max_call_dt: string | null;
  };
}

// Timing's "recommended windows" rollup (CC-PROTOTYPE-AUDIT.md gap #2) --
// the prototype's actual final deliverable: **one row per state**, with
// individually-qualifying hours collapsed into compact ranges (matching
// cc_engine2.py's own output shape), not one row per hour. Mirrors
// get_timing_recommended_windows.

// One collapsed range of consecutive qualifying hours, e.g. hours
// [9, 10, 11] become a single window "9am-12pm".
export interface TimingRecommendedWindowRange {
  start_hour: number;
  end_hour_exclusive: number;
  hour_label: string;
  eastern_hour_label: string | null;
  central_hour_label: string | null;
  mountain_hour_label: string | null;
  pacific_hour_label: string | null;
}

// The fine-grained, per-hour detail behind a state's collapsed windows --
// used for the "per-hour confidence" breakdown, matching the prototype's
// own `Per_Hour_Confidence` string.
export interface TimingRecommendedHourDetail {
  hour: number;
  hour_label: string;
  // Only "prime" or "good" ever appear here -- an hour that doesn't clear
  // one of those two bands on either metric isn't in this list at all.
  band: 'prime' | 'good';
  reasons: Array<'connect_rate' | 'value_cost_ratio'>;
  connect_rate: number | null;
  shrunk_connect_rate: number | null;
  value_cost_ratio: number | null;
  // Posterior probability this hour's true rate is genuinely above the
  // state's own average -- see stats.shrink_cell's prob_above_group_avg.
  confidence_pct: number | null;
  total_calls: number;
}

export interface TimingRecommendedWindowsStateRow {
  state: string;
  total_calls: number;
  connect_rate: number | null;
  // Same "raw aggregate across every hour for this state" approach as
  // connect_rate above, just for the dollar ratio -- see service.py's
  // _compute_value_cost_cells docstring.
  value_cost_ratio: number | null;
  band: 'prime' | 'good';
  reasons: Array<'connect_rate' | 'value_cost_ratio'>;
  windows: TimingRecommendedWindowRange[];
  windows_label: string;
  eastern_windows_label: string;
  central_windows_label: string;
  mountain_windows_label: string;
  pacific_windows_label: string;
  hour_details: TimingRecommendedHourDetail[];
  hour_detail_label: string;
  window_count: number;
  qualifying_hour_count: number;
  avg_confidence_pct: number | null;
}

export interface TimingRecommendedWindows {
  states: TimingRecommendedWindowsStateRow[];
  states_with_no_recommended_hour: number;
  min_cell_calls: number;
  min_cell_deals: number;
  ci_level: number;
  date_range: {
    min_call_dt: string | null;
    max_call_dt: string | null;
  };
  timezone_note: string;
}

// Plan screen. Mirrors get_plan_capacity. Build step 4: demand-only --
// "required_agents" is demand converted into agent-count units via a
// single global dials-per-agent-hour baseline, not a real staffing plan.
// There's no agent shift/licence data to plan a true gap against yet.

export type PlanDemandTier = 'low' | 'below_average' | 'above_average' | 'high';

export interface PlanHour {
  hour: number;
  hour_label: string;
  n_days: number;
  total_calls: number;
  total_connects: number;
  connect_rate: number | null;
  avg_calls_per_day: number;
  avg_connects_per_day: number;
  staffed_agents: number;
  // How many agents recent call volume (last PLAN_RECENT_DEMAND_LOOKBACK_DAYS
  // days -- see PlanScope.window) needs in this hour. See
  // `service._compute_recent_demand`. Single "required" concept as of
  // 2026-08-12 -- there used to also be a separately-plotted full-history
  // version of this line; retired (see `get_plan_capacity`'s own
  // docstring) once this recent, connect-rate-weighted version proved
  // both more current AND less noisy than the raw full-history one.
  required_agents: number | null;
  shortfall_agents: number | null;
  surplus_agents: number | null;
  contacts_per_added_agent_hour: number | null;
  // Purely a chart-shading aid -- this hour's call volume quartile
  // relative to the *other hours on this same chart*, not a universal
  // cutoff. See `_demand_tiers_by_hour` in service.py.
  demand_tier: PlanDemandTier | null;
}

export interface PlanRecentDemand {
  total_weekly_calls: number;
  lookback_days: number;
  states_without_timezone: number;
  states_without_rate_data: number;
  note: string;
}

export interface PlanScopeWindow {
  label: string;
  lookback_days: number;
  min_call_dt: string | null;
  max_call_dt: string | null;
}

export interface PlanScope {
  window: PlanScopeWindow;
}

export interface PlanConcentrationGuardrail {
  peak_share_ceiling_pct: number;
  peak_hour: number | null;
  peak_hour_label: string | null;
  peak_share_pct: number | null;
  breaches_ceiling: boolean;
  note: string;
}

export interface PlanGapCostDollarBasis {
  avg_premium_per_cas: number;
  avg_commission_per_cas: number;
  is_measured: boolean;
}

export interface PlanGapCost {
  missed_connects_per_week: number;
  connect_to_cas_pct: number | null;
  estimated_cas_lost_per_week: number | null;
  estimated_premium_lost_per_week: number | null;
  estimated_commission_lost_per_week: number | null;
  dollar_basis: PlanGapCostDollarBasis;
}

export interface PlanHeadlineBiggestGap {
  hour: number;
  hour_label: string;
  shortfall_agents: number;
}

export interface PlanHeadline {
  fill_pct: number | null;
  hours_short_count: number;
  hours_tracked_count: number;
  biggest_gap: PlanHeadlineBiggestGap | null;
  estimated_dollars_at_stake_per_week: number | null;
}

export interface PlanCapacity extends SectionCacheMeta {
  hours: PlanHour[];
  global_dials_per_agent_hour: number | null;
  weekday_operating_days: number;
  // Real measured full-week contacts (all 7 days, including the small
  // weekend contribution) -- not `avg_connects_per_day * 7`.
  actual_weekly_contacts: number;
  weekend_note: string;
  headline: PlanHeadline;
  gap_cost: PlanGapCost;
  // Optional defensively: an already-cached payload computed before this
  // field existed (`plan` section's TTL is several hours) won't have it
  // until its cache row naturally expires and recomputes.
  scope?: PlanScope;
  recent_demand: PlanRecentDemand;
  concentration_guardrail: PlanConcentrationGuardrail;
}

// Plan's "recommended-hours coverage" check -- are the hours Timing
// recommends actually staffed on the roster? Mirrors
// service.py::get_plan_recommended_coverage.

export interface PlanRecommendedCoverageMiss {
  state: string;
  hour_label: string;
  eastern_hour_label: string;
  band: 'prime' | 'good';
  confidence_pct: number | null;
  total_calls: number;
}

export interface PlanRecommendedCoverage {
  checked_count: number;
  covered_count: number;
  coverage_pct: number | null;
  out_of_window_count: number;
  operating_window_label: string;
  biggest_misses: PlanRecommendedCoverageMiss[];
  biggest_misses_total_count: number;
  windows_computed_at: string | null;
  schedule_computed_at: string | null;
  note: string;
}

// Plan's "roster" slot: a best-effort agent shift schedule, NOT the Queue
// (still deferred) -- a staffing plan (which hours each agent should be
// on the floor), not a live per-lead routing engine. Mirrors
// backend/services/contact_optimization/agent_schedule.py.

export type HourStatus = 'on' | 'break' | 'off';

export interface AgentShift {
  agent_full_nm: string;
  agent_title: string | null;
  agent_departement: string | null;
  // "real" = an imported, human-authored schedule row (see
  // AgentScheduleResponse.real_schedule_source_files). "inferred" = the
  // best-effort, call-history-based guess used when no real row exists
  // for this agent.
  source: 'real' | 'inferred';
  assigned_shift_label: string | null;
  home_tz: string | null;
  total_historical_calls: number;
  sufficient_history: boolean;
  shift_start_hour: number | null;
  shift_start_label: string | null;
  shift_end_hour: number | null;
  shift_end_label: string | null;
  break_hour: number | null;
  break_label: string | null;
  // 24-length, index = hour of day on the reference clock.
  hours: HourStatus[];
}

export interface ScheduleCoverageHour {
  hour: number;
  hour_label: string;
  agents_on_shift: number;
  required_agents: number | null;
  gap: number | null;
}

export interface AgentScheduleResponse {
  reference_timezone: string;
  lookback_days: number;
  shift_work_hours: number;
  break_hours: number;
  included_departments: string[];
  global_dials_per_agent_hour: number | null;
  real_schedule_agent_count: number;
  inferred_schedule_agent_count: number;
  real_schedule_source_files: string[];
  real_schedule_imported_at: string | null;
  agents: AgentShift[];
  coverage: ScheduleCoverageHour[];
  notes: string[];
}

// Roster's second view: a "maximum-coverage" reshuffle that keeps each
// agent's own shift length but searches for a better START time for them
// against the recent-volume demand curve. Mirrors
// backend/services/contact_optimization/roster_optimizer.py +
// service.py::get_plan_roster_optimized. Distinct from AgentScheduleResponse
// above (the "as-given" view, unchanged real/inferred shifts).

export interface OptimizedRosterAssignment {
  agent_full_nm: string;
  home_tz: string | null;
  source: 'real' | 'inferred';
  span_hours: number;
  current_start_hour: number;
  // Under split scheduling (the default as of 2026-08-03), this agent's
  // hours may not be contiguous -- this is their FIRST on-hour of the
  // day, a display stand-in for "start," not the whole story. The
  // `hours` array below is the actual source of truth.
  optimized_start_hour: number;
  // Positive = moved later in the day, negative = moved earlier. 0 means
  // this agent's best slot IS their current one. Computed from the first
  // on-hour above, so it's also only a rough diff once split.
  moved_hours: number;
  // 24-length, index = hour of day on the reference clock -- same shape
  // as AgentShift.hours, so the same Gantt-row renderer can draw both.
  // "break" entries can now be a real, solver-chosen mid-shift gap, not
  // just an inferred agent's single cosmetic break hour.
  hours: HourStatus[];
  // True if this agent's on-hours aren't one contiguous block -- i.e. the
  // solver actually split their shift. Absent/false under the continuous
  // solver (allow_split_shifts=False), which never splits anyone.
  is_split?: boolean;
  // Lossless shift geometry (split solver only; absent under the
  // continuous solver). `hours` above rounds a 30-minute break up to a
  // whole hour -- these carry the solver's true half-hour-precise
  // boundaries so the grid can draw and label breaks at real size.
  segments?: RosterShiftSegment[];
  // This agent's own real total WORK hours (what the optimizer
  // preserves). `break_hours` is what the solver added on top to satisfy
  // the 5-hour continuous-work cap, so day_end - day_start =
  // work_hours + break_hours, a LONGER elapsed day than they work today.
  work_hours?: number;
  break_hours?: number;
  day_start_hour?: number;
  day_end_hour?: number;
}

/** Which states to work first in a given hour -- ranked by connect rate
 * among states carrying enough of that hour's volume to be worth an
 * agent's time. Same list every weekday (see the backend helper). */
export interface StatePriorityEntry {
  state: string;
  connect_rate: number;
  local_hour: number | null;
  attempts_per_week: number;
  share_of_hour: number;
}

export interface RosterShiftSegment {
  kind: 'work' | 'break';
  /** Reference-clock decimal hours, e.g. 11.5 = 11:30am. */
  start: number;
  end: number;
}

export interface OptimizedRoster {
  assignments: OptimizedRosterAssignment[];
  shortfall_agent_hours_before: number | null;
  shortfall_agent_hours_after: number | null;
  coverage_by_hour_before: number[] | null;
  coverage_by_hour_after: number[] | null;
  // 24-length, index = hour of day on the reference clock -- the recent-volume
  // target the reshuffle above was scored/solved against.
  target_by_hour: number[];
  solver_status: string;
  agents_moved_count: number;
  // How many agents actually got a non-contiguous (split) schedule.
  // Absent/0 under the continuous solver.
  agents_split_count?: number;
  notes: string[];
  // Estimated total connects/day for each view's schedule -- same total
  // agent-hours in both, so any difference here is purely "did moving
  // people to different hours convert more of them," per the same
  // state-weighted connect-rate curve the demand target itself uses.
  // Null if there wasn't enough rate data to estimate.
  expected_connects_per_day_before: number | null;
  expected_connects_per_day_after: number | null;
  // The same comparison as a 0..1 rate (connects / dials) rather than a
  // raw count -- moves in lockstep with the pair above since total
  // dials/day is identical in both schedules, just a more familiar unit
  // for the same fact.
  expected_connect_rate_before: number | null;
  expected_connect_rate_after: number | null;
}

// The hour columns the Roster grid should actually draw. Deliberately
// NOT the same as PlanCapacity.hours (that's hard-capped to 6am-5pm,
// all the real historical data covers) -- the recent-volume target
// and reshuffled shifts here can run well past that, so this is
// computed from what's actually non-zero (target/coverage/any agent's
// on-shift hours), padded by an hour on each side. See
// `service._roster_active_hours`.
export interface RosterHour {
  hour: number;
  hour_label: string;
}

/** Shift lengths the roster-view toggle offers. Mirrors the backend's
 * SUPPORTED_SHIFT_WORK_HOURS -- the route rejects anything else. */
export const SUPPORTED_SHIFT_WORK_HOURS = [8, 10, 12] as const;
export type ShiftWorkHours = (typeof SUPPORTED_SHIFT_WORK_HOURS)[number];

export interface PlanRosterOptimizedResponse {
  reference_timezone: string;
  /** The shift length THIS roster was solved for. Read this rather than
   * assuming: the contact-rate tile picks its scenario from it, and when
   * the two disagree the tile describes a roster other than the grid
   * below it. */
  shift_work_hours: number;
  roster_hours: RosterHour[];
  as_given: {
    agents: AgentShift[];
    notes: string[];
  };
  optimized: OptimizedRoster;
  recent_demand: {
    total_weekly_calls: number;
    lookback_days: number;
    note: string;
  };
  /** Keyed by reference-clock hour. Absent for hours where no state
   * carries enough of the volume to be worth naming. */
  state_priority_by_reference_hour?: Record<string, StatePriorityEntry[]>;
}

// Queue screen (build step 7, Phase 1 only): "what is being dialed now,
// and why". Ranks which lead to call and when -- does NOT assign a
// specific agent (that's the roster, Phase 2, still deferred). Mirrors
// backend/services/contact_optimization/queue.py::get_queue.

export type QueueRowStatus = 'dialing_now' | 'held' | 'blocked' | 'priority';

export interface QueueRow {
  hubspot_contact_id: string;
  masked_id: string;
  lead_label: string;
  lead_sublabel: string | null;
  state: string;
  status: QueueRowStatus;
  reason: string;
  call_hour_label: string | null;
  pickup_pct: number | null;
  current_pct: number | null;
  best_pct: number;
  best_hour_label: string;
  value_score: number | null;
  deal_rate: number;
  expected_value: number | null;
  attempt_number: number;
  talk_headline: string;
  talk_detail: string;
  talk_facts: string[];
}

export interface QueueMetrics {
  /** Which hour the screen is answering for. Normally the current hour;
   * when nobody is on the floor it rolls forward to the next hour the
   * roster has someone working, so the numbers below are never a wall of
   * zeroes just because it is 6am. `reference_is_tomorrow` matters: past
   * midnight the expected_* fields are deliberately null, because
   * tomorrow's queue will contain leads that have not arrived yet. */
  reference_hour: number;
  reference_hour_label: string;
  reference_is_now: boolean;
  reference_is_tomorrow: boolean;
  total_eligible_population: number;
  excluded_no_timezone: number;
  dialing_now: number;
  held_for_later: number;
  outside_call_window: number;
  attempt_cap_blocked: number;
  random_sample_pct: number;
  capacity_now: number;
  agents_on_now: number;
  global_dials_per_agent_hour: number | null;
  // Model-predicted outcomes for the leads being dialed this hour, not
  // measured results. 0..1 fraction for the rate; counts for the rest.
  expected_connect_rate_dialing_now: number | null;
  expected_connects_this_hour: number | null;
  expected_deals_this_hour: number | null;
  avg_attempt_number_dialing_now: number | null;
}


export interface QueueScopePopulation {
  label: string;
  population_window_days: number;
  total_eligible: number;
}

export interface QueueScopeCapacity {
  label: string;
  as_of: string;
  reference_hour_label: string;
  agents_on_now: number;
}

export interface QueueScope {
  population: QueueScopePopulation;
  capacity: QueueScopeCapacity;
}

export interface QueueResponse extends SectionCacheMeta {
  available: boolean;
  note?: string;
  rows: QueueRow[];
  rows_shown?: number;
  rows_total?: number;
  metrics: QueueMetrics | Record<string, never>;
  scope?: QueueScope;
  model_version?: number | null;
  notes: string[];
}

/** One row of the customer's scorecard sheet (TY_Scorecard.xlsx).
 *
 * `value` is always the last point of `trend`, so a headline and its
 * sparkline cannot disagree. `unavailable_reason` is set instead of a
 * value when the warehouse cannot support the metric -- a blank and a
 * zero mean different things and only one of them is true here. */
export interface ScorecardMetric {
  id: string;
  name: string;
  definition: string;
  owner: string;
  pillar: string;
  primer_outcomes: string[];
  format: 'percent' | 'ratio' | 'count' | 'money' | 'days';
  /** Plain-English calculation, shown when hovering the metric name. */
  how: string | null;
  /** What an agent's figure is measured over ("leads called",
   * "voicemails left"), used to label the per-agent list's last column. */
  denominator_label: string | null;
  note: string | null;
  unavailable_reason: string | null;
  value: number | null;
  /** Like-for-like points, oldest first, ending with the selected
   * period. Each spans the same length as the selection, so every point
   * is comparable to `value` -- which is literally the last of them. The
   * matching date ranges are on the response's `trend.periods`. */
  trend: (number | null)[] | null;
  agents: ScorecardAgentValue[] | null;
}

export interface ScorecardAgentValue {
  agent: string;
  value: number;
  /** What the metric is measured over for this agent -- shown alongside
   * so a rate built on three leads reads as exactly that. */
  denominator: number;
}

export interface ScorecardPillar {
  name: string;
  owner: string;
  metrics: ScorecardMetric[];
}

export interface ScorecardPeriod {
  start: string;
  end: string;
  days: number;
  /** Server's name for the range: "July 2026", "Q2 2026", "3 days". */
  label: string;
}

/** How the trend was built for the selected period. Each point spans the
 * same length as the selection, which is what makes them comparable --
 * see `scorecard.trend_periods`. */
export interface ScorecardTrend {
  grain: 'year' | 'quarter' | 'month' | 'week' | 'weekday_span' | 'span';
  points: number;
  periods: { start: string; end: string }[];
  /** Full method, e.g. "Same days over the last 7 weeks". */
  description: string;
  /** Horizon only, for the trend column's header: "Last 7 weeks". */
  short_label: string;
}

export interface ScorecardResponse extends SectionCacheMeta {
  period: ScorecardPeriod;
  trend: ScorecardTrend;
  pillars: ScorecardPillar[];
  metric_count: number;
  unavailable_metric_ids: string[];
  agent_count: number;
}

// --- Scorecard: cohort by arrival day --------------------------------------
//
// Counts each lead's first CONNECT, not its first call -- so these rates run
// higher than the Pulse tab's contact-speed table on the same leads. See
// services/contact_optimization/scorecard_cohort.py.

export type CohortGrain = 'day' | 'week' | 'month';

/** `d0`..`d6`, plus the three terminal classes. `contacted` is a filter-only
 * convenience meaning "any of d0..d6 or later". */
export type CohortOutcome =
  | 'd0' | 'd1' | 'd2' | 'd3' | 'd4' | 'd5' | 'd6'
  | 'later' | 'dialed_never' | 'never_dialed';

export interface ScorecardCohortRow {
  /** Clamped to the selected range, so at week or month grain an edge
   * bucket reports only the days actually in scope. */
  bucket_start: string;
  bucket_end: string;
  /** Named for the bucket it belongs to, e.g. "Week of 27 Jul", even when
   * the range clips it. */
  label: string;
  /** True when the range cut this bucket short at either end. */
  partial: boolean;
  /** False while the bucket is too recent for its later columns to be
   * complete; those come back null rather than 0. */
  mature: boolean;
  leads: number;
  /** Keyed '0'..'6'. Null means "not yet knowable", never "zero". */
  offsets: Record<string, number | null>;
  later: number | null;
  dialed_never: number;
  never_dialed: number;
  total_contacted: number;
}

/** Extends SectionCacheMeta because the default window is served from the
 * section cache; a hand-picked range computes live and arrives without those
 * stamps, which is how the UI tells the two apart. */
export interface ScorecardCohortResponse extends SectionCacheMeta {
  grain: CohortGrain;
  max_offset: number;
  reachability_days: number;
  data_as_of: string;
  period: { start: string; end: string };
  rows: ScorecardCohortRow[];
}

export interface ScorecardCohortLead {
  /** Full HubSpot contact id. Not masked: it is the only handle on a lead
   * this warehouse offers, and a truncated one cannot be looked up. */
  contact_id: string;
  /** Product and state descriptor today. Becomes a real name if a live
   * HubSpot read ever lands -- no name, email or phone exists in the
   * warehouse. */
  label: string;
  state: string | null;
  lead_dt: string;
  attempts: number;
  first_connect_offset: number | null;
  connected_by: string | null;
  last_attempt_dt: string | null;
  /** Date plus the lead's local hour, e.g. "2026-08-20 2pm". The warehouse
   * has no minutes, so an hour is the finest resolution available. */
  last_attempt_at: string | null;
  /** Lead-local hour of the first connect, e.g. "2pm". */
  connect_hour: string | null;
  outcome: CohortOutcome;
}

export interface ScorecardCohortLeadsResponse {
  total: number;
  limit: number;
  offset: number;
  bucket_start: string;
  bucket_end: string;
  outcome: string;
  leads: ScorecardCohortLead[];
}




/** Queue's expected connect rate over time, rather than right now.
 *
 * Recorded hourly by the cache warmer as a side effect of a Queue
 * recompute. Cannot be backfilled -- reconstructing an old prediction
 * would need the model as it was then -- so `available` is false until the
 * series has filled forward for a while. */
export interface ExpectationTrendPoint {
  date: string;
  expected_connect_rate: number;
  hours: number;
}

export interface ExpectationTrend {
  available: boolean;
  note?: string | null;
  window_days: number;
  hours_recorded: number;
  days_recorded?: number;
  trailing_average?: number | null;
  latest_day_average?: number | null;
  series?: ExpectationTrendPoint[];
  model_versions?: string[];
  notes?: string[];
}


/** Expected contact rate -- per LEAD, across capacity scenarios.
 *
 * Of the leads arriving today, the share we expect to reach at least once
 * within the horizon. Distinct from the connect rate on the Queue tab,
 * which is per DIAL and asks whether a single call gets answered.
 *
 * `scenarios` varies the roster's shift length, so changing the agent-hours
 * limit moves the number. `uncapped` is the ceiling with no agent limit at
 * all, bounded only by the per-day cadence cap and the calling window --
 * a limit rather than a plan, since it needs many times the current floor.
 * `backlog_contact_rate` answers a different question again: how much is
 * still recoverable from the pile we are already holding. */
export interface ContactRateScenario {
  label: string;
  hours_per_agent: number | null;
  dials_per_day: number;
  contact_rate: number | null;
  same_day_first_attempts: number;
  agents_required?: number | null;
}

export interface ExpectedContactRate extends SectionCacheMeta {
  available: boolean;
  note?: string | null;
  expected_contact_rate: number | null;
  observed_contact_rate: number | null;
  /** How many leads the observed rate was measured on, and when they
   * arrived. A lead only counts once its whole reachability window has
   * closed, so this is never "today". */
  observed_leads?: number;
  observed_window_days_ago?: { from: number; to: number };
  /** How often one lead may be called in a single day -- the constraint
   * that bounds the best-possible number once staffing stops mattering. */
  max_attempts_per_day?: number;
  scenarios: ContactRateScenario[];
  uncapped: ContactRateScenario | null;
  backlog_contact_rate: number | null;
  backlog_leads: number;
  horizon_days: number;
  arrivals_per_day: number;
  agents: number;
  dials_per_agent_hour: number | null;
  leads_already_contacted: number;
  hazard_by_attempt: Record<string, number>;
  hazard_by_day: Record<string, number>;
  hazard_dials_fit_on: number;
  notes: string[];
}
